import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, CalendarClock, Plug, Plus, RefreshCw, Target, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { fitstreakApi } from "../api";
import { formatLocalDate } from "../format";
import type { IntegrationStatus, Preferences } from "../types";

// Countdown anchored to a supplied "today" (the server's APP_TIMEZONE date), so
// it matches the dashboard's countdown rather than drifting with the browser zone.
function daysUntil(date: string, today: string): number {
	const a = Date.parse(`${date}T00:00:00Z`);
	const b = Date.parse(`${today}T00:00:00Z`);
	return Math.round((a - b) / 86_400_000);
}

function RacesCard() {
	const qc = useQueryClient();
	const races = useQuery({
		queryKey: ["fitstreak", "races"],
		queryFn: () => fitstreakApi.listRaces(),
	});
	const [name, setName] = useState("");
	const [date, setDate] = useState("");

	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["fitstreak", "races"] });
		qc.invalidateQueries({ queryKey: ["fitstreak", "dashboard"] });
	};

	const add = useMutation({
		mutationFn: () => fitstreakApi.createRace({ input: { name, raceDate: date } }),
		onSuccess: () => {
			toast.success("Race added");
			setName("");
			setDate("");
			invalidate();
		},
		meta: { errorMessage: "Failed to add race" },
	});

	const remove = useMutation({
		mutationFn: (id: string) => fitstreakApi.removeRace({ params: { id } }),
		onSuccess: () => {
			toast.success("Race removed");
			invalidate();
		},
		meta: { errorMessage: "Failed to remove race" },
	});

	// "today" in the app's home timezone — shared (cached) with the dashboard so
	// both countdowns agree regardless of the viewer's browser zone.
	const dashboard = useQuery({
		queryKey: ["fitstreak", "dashboard"],
		queryFn: () => fitstreakApi.dashboard(),
	});
	const today = dashboard.data?.today ?? new Date().toLocaleDateString("en-CA");

	const list = races.data ?? [];

	return (
		<section className="flex flex-col gap-4 rounded-lg border border-hairline bg-surface-1 p-4">
			<div className="flex items-center gap-2">
				<CalendarClock className="size-4 text-primary" />
				<h2 className="card-title text-foreground">Goal races</h2>
			</div>

			{list.length > 0 ? (
				<div className="flex flex-col divide-y divide-hairline overflow-hidden rounded-md border border-hairline">
					{list.map((race) => {
						const d = daysUntil(race.raceDate, today);
						return (
							<div key={race.id} className="flex items-center gap-3 bg-surface-2 px-3.5 py-2.5">
								<div className="flex min-w-0 flex-1 flex-col">
									<span className="truncate text-body-sm font-medium">{race.name}</span>
									<span className="text-meta text-ink-subtle">
										{formatLocalDate(race.raceDate)}
									</span>
								</div>
								<span className="flex-none text-meta tabular-nums text-ink-muted">
									{d >= 0
										? `in ${d} day${d === 1 ? "" : "s"}`
										: `${-d} day${d === -1 ? "" : "s"} ago`}
								</span>
								<ConfirmDialog
									title="Remove race?"
									description={`Remove "${race.name}" from your goals?`}
									confirmLabel="Remove"
									destructive
									onConfirm={() => remove.mutate(race.id)}
								>
									<button
										type="button"
										aria-label="Remove race"
										className="flex size-7 flex-none items-center justify-center rounded-md text-ink-subtle hover:bg-destructive/10 hover:text-destructive"
									>
										<Trash2 className="size-4" />
									</button>
								</ConfirmDialog>
							</div>
						);
					})}
				</div>
			) : (
				<p className="text-body-sm text-ink-subtle">
					No races yet. Add your goal races to see countdowns.
				</p>
			)}

			<div className="flex flex-col gap-2 border-t border-hairline pt-4 sm:flex-row sm:items-end">
				<div className="flex flex-1 flex-col gap-1.5">
					<Label htmlFor="race-name">Race name</Label>
					<Input
						id="race-name"
						placeholder="Chicago Marathon"
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<Label htmlFor="race-date">Date</Label>
					<Input
						id="race-date"
						type="date"
						value={date}
						onChange={(e) => setDate(e.target.value)}
					/>
				</div>
				<Button disabled={!name.trim() || !date || add.isPending} onClick={() => add.mutate()}>
					<Plus className="size-4" /> Add
				</Button>
			</div>
		</section>
	);
}

function PreferencesCard() {
	const qc = useQueryClient();
	const prefs = useQuery({
		queryKey: ["fitstreak", "preferences"],
		queryFn: () => fitstreakApi.getPreferences(),
	});

	const update = useMutation({
		mutationFn: (patch: Partial<Pick<Preferences, "weeklyActiveDaysTarget" | "nudgeEnabled">>) =>
			fitstreakApi.updatePreferences({ input: patch }),
		onSuccess: () => {
			toast.success("Preferences saved");
			qc.invalidateQueries({ queryKey: ["fitstreak", "preferences"] });
			qc.invalidateQueries({ queryKey: ["fitstreak", "dashboard"] });
		},
		meta: { errorMessage: "Failed to save" },
	});

	const p = prefs.data;

	return (
		<section className="flex flex-col gap-4 rounded-lg border border-hairline bg-surface-1 p-4">
			<h2 className="card-title text-foreground">Goals & reminders</h2>

			<div className="flex items-center justify-between gap-4">
				<div className="flex flex-col">
					<span className="text-body-sm font-medium">Weekly active-day target</span>
					<span className="text-meta text-ink-subtle">How many days a week you aim to move.</span>
				</div>
				<Select
					value={String(p?.weeklyActiveDaysTarget ?? 5)}
					onValueChange={(v) => update.mutate({ weeklyActiveDaysTarget: Number(v) })}
				>
					<SelectTrigger size="sm" className="w-20">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{[1, 2, 3, 4, 5, 6, 7].map((n) => (
							<SelectItem key={n} value={String(n)}>
								{n}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="flex items-center justify-between gap-4 border-t border-hairline pt-4">
				<div className="flex flex-col">
					<span className="text-body-sm font-medium">Daily reminder</span>
					<span className="text-meta text-ink-subtle">
						Emails you in the evening if you haven't moved that day.
					</span>
				</div>
				<Button
					variant={p?.nudgeEnabled ? "default" : "outline"}
					size="sm"
					disabled={!p || update.isPending}
					onClick={() => update.mutate({ nudgeEnabled: !p?.nudgeEnabled })}
				>
					{p?.nudgeEnabled ? (
						<>
							<Bell className="size-3.5" /> On
						</>
					) : (
						<>
							<BellOff className="size-3.5" /> Off
						</>
					)}
				</Button>
			</div>
		</section>
	);
}

function GarminConnection({ status }: { status: IntegrationStatus }) {
	const qc = useQueryClient();
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");

	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["fitstreak", "integrations"] });
		qc.invalidateQueries({ queryKey: ["fitstreak", "dashboard"] });
	};

	const connect = useMutation({
		mutationFn: () => fitstreakApi.connectGarmin({ input: { username, password } }),
		onSuccess: () => {
			toast.success("Garmin connected");
			setUsername("");
			setPassword("");
			invalidate();
		},
		meta: { errorMessage: "Failed to connect Garmin" },
	});

	const disconnect = useMutation({
		mutationFn: () => fitstreakApi.disconnectGarmin(),
		onSuccess: () => {
			toast.success("Garmin disconnected");
			invalidate();
		},
		meta: { errorMessage: "Failed to disconnect" },
	});

	const sync = useMutation({
		mutationFn: () => fitstreakApi.sync(),
		onSuccess: () => {
			toast.success("Sync started");
			invalidate();
		},
		meta: { errorMessage: "Sync failed" },
	});

	if (status.connected) {
		return (
			<div className="flex flex-col gap-2 rounded-md border border-hairline bg-surface-2 p-3.5">
				<div className="flex items-center justify-between gap-2">
					<div className="flex min-w-0 flex-col">
						<span className="text-body-sm font-medium">Garmin Connect</span>
						<span className="truncate text-meta text-ink-subtle">
							{status.username}
							{status.lastSyncAt
								? ` · last sync ${new Date(status.lastSyncAt).toLocaleString()}`
								: " · not synced yet"}
						</span>
					</div>
					<Badge tone="success" dot="var(--color-success)">
						Connected
					</Badge>
				</div>
				{status.status === "error" && status.lastError ? (
					<span className="text-meta text-destructive">Last error: {status.lastError}</span>
				) : null}
				<div className="flex gap-2">
					<Button
						size="sm"
						variant="outline"
						disabled={sync.isPending}
						onClick={() => sync.mutate()}
					>
						<RefreshCw className={`size-3.5 ${sync.isPending ? "animate-spin" : ""}`} /> Sync now
					</Button>
					<ConfirmDialog
						title="Disconnect Garmin?"
						description="This removes your stored Garmin credentials. Synced activities stay in your log."
						confirmLabel="Disconnect"
						destructive
						onConfirm={() => disconnect.mutate()}
					>
						<Button size="sm" variant="ghost">
							Disconnect
						</Button>
					</ConfirmDialog>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-2 rounded-md border border-hairline bg-surface-2 p-3.5">
			<span className="text-body-sm font-medium">Garmin Connect</span>
			<p className="text-meta text-ink-subtle">
				Garmin has no OAuth, so this uses your Garmin Connect login. Your password is encrypted at
				rest and only used to sync activities.
			</p>
			<Input
				placeholder="Garmin email"
				autoComplete="off"
				value={username}
				onChange={(e) => setUsername(e.target.value)}
			/>
			<Input
				type="password"
				placeholder="Garmin password"
				autoComplete="off"
				value={password}
				onChange={(e) => setPassword(e.target.value)}
			/>
			<Button
				size="sm"
				className="self-start"
				disabled={!username.trim() || !password || connect.isPending}
				onClick={() => connect.mutate()}
			>
				{connect.isPending ? "Connecting…" : "Connect Garmin"}
			</Button>
		</div>
	);
}

function IntegrationsCard() {
	const integrations = useQuery({
		queryKey: ["fitstreak", "integrations"],
		queryFn: () => fitstreakApi.integrations(),
	});
	const garmin = integrations.data?.sources.find((s) => s.id === "garmin");

	return (
		<section className="flex flex-col gap-4 rounded-lg border border-hairline bg-surface-1 p-4">
			<div className="flex items-center gap-2">
				<Plug className="size-4 text-primary" />
				<h2 className="card-title text-foreground">Connected apps</h2>
			</div>
			{garmin ? <GarminConnection status={garmin} /> : null}
			<div className="flex items-center justify-between rounded-md border border-dashed border-hairline px-3.5 py-3">
				<span className="text-body-sm text-ink-muted">TrainingPeaks</span>
				<span className="text-meta text-ink-subtle">Coming soon</span>
			</div>
		</section>
	);
}

export function GoalsPage() {
	return (
		<>
			<PageHeader
				icon={Target}
				title="Goals & races"
				subtitle="Set your weekly targets, track upcoming races, and manage connected apps."
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-2xl space-y-5">
					<RacesCard />
					<IntegrationsCard />
					<PreferencesCard />
				</div>
			</PageBody>
		</>
	);
}
